// src/pages/private/settings/securityTabs/SecurityMfa.jsx

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { SafetyOutlined, StarOutlined } from '@ant-design/icons';
import { useAuth } from '../../../../authContext';
import { preloadUser } from '../../../../preloadUser';
import SecurityModal from './SecurityModal';
import Modal from 'react-modal';
import ToggleSwitchTrack from '../../../../components/tools/ToggleSwitchTrack';

function SecurityMfa() {
  const { t } = useTranslation();
  const {
    email,
    hasMfa,
    mfaType,
    setHasMfa,
    setLanguage,
    setTimezone,
    setGcuAccepted,
    setLastAcceptanceDate,
  } = useAuth();
  const [backupCodes, setBackupCodes] = useState([]);
  const [showBackupCodesModal, setShowBackupCodesModal] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [qrCode, setQrCode] = useState('');
  const [secretCode, setSecretCode] = useState('');
  const [globalError, setGlobalError] = useState('');
  const [mfaAppCode, setMfaAppCode] = useState('');
  const [mfaMailCode, setMfaMailCode] = useState('');
  const [mfaError, setMfaError] = useState('');
  const [mfaEnabled, setMfaEnabled] = useState(hasMfa);

  useEffect(() => {
    setMfaEnabled(hasMfa);
  }, [hasMfa]);

  const handleToggle = async (enabled) => {
    setMfaEnabled(enabled);

    if (enabled) {
      try {
        const response = await axios.post('/api/mfa/setup', { email, type: mfaType }, { withCredentials: true });
        setQrCode(response.data.qrCode);
        setSecretCode(response.data.secretCode);
        setIsModalOpen(true);
      } catch (error) {
        console.error('Error configuring MFA:', error);
        setGlobalError('Error enabling MFA.');
      }
    } else {
      try {
        const response = await axios.post('/api/mfa/disable', { email, type: mfaType }, { withCredentials: true });
        if (response.status === 200) {
          const fullUser = await preloadUser({ isLoggedIn: true });

          if (fullUser) {
            setHasMfa(fullUser.hasMfa);
            setLanguage(fullUser.language);
            setTimezone(fullUser.timezone);
            setGcuAccepted(fullUser.gcuAccepted);
            setLastAcceptanceDate(fullUser.lastAcceptanceDate);
          }
        } else {
          setGlobalError('Error disabling MFA.');
        }
      } catch (error) {
        console.error('Error disabling MFA:', error);
        setGlobalError('Error disabling MFA.');
      }
    }
  };

  const generateBackupCodes = async () => {
    try {
      const response = await axios.post('/api/mfa/generate-backup-codes', {}, { withCredentials: true });
      if (response.data && Array.isArray(response.data.backupCodes)) {
        setBackupCodes(response.data.backupCodes);
        setShowBackupCodesModal(true);
      }
    } catch (error) {
      console.error('Error generating new backup codes:', error);
    }
  };

  const handlePrintBackupCodes = () => {
    const printContent = backupCodes.join('\n');
    const printWindow = window.open('', '_blank');
    printWindow.document.open();
    printWindow.document.write('<pre>' + printContent + '</pre>');
    printWindow.document.close();
    printWindow.print();
  };

  const verifyAppMFA = async () => {
    try {
      const response = await axios.post('/api/mfa-app/verify', { token: mfaAppCode, email }, { withCredentials: true });
      if (response.data.verified) {
        const codesResponse = await axios.post('/api/mfa/generate-backup-codes', {}, { withCredentials: true });
        setBackupCodes(codesResponse.data.backupCodes);
        setShowBackupCodesModal(true);
        setQrCode(null);
        setIsModalOpen(false);
        const fullUser = await preloadUser({ isLoggedIn: true });

        if (fullUser) {
          setHasMfa(fullUser.hasMfa);
          setLanguage(fullUser.language);
          setTimezone(fullUser.timezone);
          setGcuAccepted(fullUser.gcuAccepted);
          setLastAcceptanceDate(fullUser.lastAcceptanceDate);
        }
        setMfaError('');
      } else {
        setMfaError(t('IncorrectCodeError'));
      }
    } catch (error) {
      console.error('Error verifying the Two-Step Authentication:', error);
      setMfaError(t('MFAVerificationError'));
    }
  };

  const sendMfaMail = async () => {
    try {
      const response = await axios.post('/api/mfa-email/sendMfaEmailToken', {}, { withCredentials: true });
      if (response.status === 200) {
        setGlobalError('Code sent successfully. Please check your email.');
      } else {
        setGlobalError('Error sending the code. Please try again.');
      }
    } catch (error) {
      console.error('Error sending the code:', error);
      setGlobalError('Error sending the code. Please try again.');
    }
  };

  const verifyMfaEmailCode = async () => {
    try {
      const response = await axios.post('/api/mfa-email/verifyMfaEmailToken', { token: mfaMailCode }, { withCredentials: true });
      if (response.status === 200) {
        setGlobalError('MFA verified successfully.');
        setIsModalOpen(false);
        const fullUser = await preloadUser({ isLoggedIn: true });

        if (fullUser) {
          setHasMfa(fullUser.hasMfa);
          setLanguage(fullUser.language);
          setTimezone(fullUser.timezone);
          setGcuAccepted(fullUser.gcuAccepted);
          setLastAcceptanceDate(fullUser.lastAcceptanceDate);
        }
      } else {
        setGlobalError('Invalid or expired MFA code. Please try again.');
      }
    } catch (error) {
      console.error('Error verifying MFA email code:', error);
      setGlobalError('Error verifying MFA email code. Please try again.');
    }
  };

  const copyToClipboard = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(secretCode)
        .then(() => {
          setGlobalError('Code copied to clipboard!');
        })
        .catch((err) => {
          console.error('Could not copy text: ', err);
        });
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target.className === 'modal-overlay') {
      setIsModalOpen(false);
    }
  };

  const mfaStatusLine = hasMfa
    ? (mfaType === 'email' ? t('MFAEnabledViaEmail') : t('MFAEnabledViaAuthenticator'))
    : t('MFACurrentlyDisabled');

  const mfaPromptLine = mfaEnabled
    ? t('MFADeactivateNotRecommended')
    : t('MFAActivateRecommended');

  return (
    <section className="security-mfa" aria-labelledby="security-mfa-heading">
      <header className="user-settings-section security-page__section-heading">
        <h3 id="security-mfa-heading" className="settings-h3">
          {t('Security2FAHeading')}
        </h3>
      </header>

      <div className="security-card security-card--mfa">
        <div className="security-card__lead">
          <div className="security-card__icon-wrap">
            <SafetyOutlined className="security-card__icon" aria-hidden />
          </div>
          <div className="security-card__copy">
            <h4 className="security-card__title">{t('securityCardMfaTitle')}</h4>
            <p className="security-card__subtitle">{t('securityCardMfaDesc')}</p>
          </div>
        </div>
        <div className="security-card__mfa-center">
          <p className="security-card__mfa-status">{mfaStatusLine}</p>
          <p className="security-card__mfa-prompt">{mfaPromptLine}</p>
        </div>
        <div className="security-card__cta security-card__cta--toggle">
          <label className="toggle toggle-switch-field security-card__toggle" htmlFor="security-mfa-toggle">
            <input
              id="security-mfa-toggle"
              type="checkbox"
              className="toggle-switch-field__input"
              checked={mfaEnabled}
              onChange={(ev) => handleToggle(ev.target.checked)}
              aria-label={mfaPromptLine}
            />
            <ToggleSwitchTrack className="security-card__toggle-track" />
          </label>
        </div>
      </div>

      <div className="security-card security-card--backup">
        <div className="security-card__lead security-card__lead--backup">
          <div className="security-card__icon-wrap">
            <StarOutlined className="security-card__icon" aria-hidden />
          </div>
          <p className="security-card__backup-text">
            {!hasMfa ? t('MFARecommendations1') : t('MFARecommendations2')}
          </p>
        </div>
        <div className="security-card__cta">
          <button type="button" className="btn-style1 btn-security-submit" onClick={generateBackupCodes}>
            {t('ManageBackupCode')}
          </button>
        </div>
      </div>

      <SecurityModal
        isModalOpen={isModalOpen}
        handleOverlayClick={handleOverlayClick}
        qrCode={qrCode}
        secretCode={secretCode}
        copyToClipboard={copyToClipboard}
        globalError={globalError}
        mfaAppCode={mfaAppCode}
        setMfaAppCode={setMfaAppCode}
        mfaMailCode={mfaMailCode}
        setMfaMailCode={setMfaMailCode}
        sendMfaMail={sendMfaMail}
        verifyMfaEmailCode={verifyMfaEmailCode}
        verifyAppMFA={verifyAppMFA}
        mfaError={mfaError}
        setIsModalOpen={setIsModalOpen}
      />

      <Modal
        isOpen={showBackupCodesModal}
        onRequestClose={() => setShowBackupCodesModal(false)}
        contentLabel="Backup Codes"
        className="mfa-modal"
        overlayClassName="modal-overlay"
      >
        <div className="modal-content-codes">
          <h2>{t('YourBackupCodes')}</h2>
          <ul>
            {Array.isArray(backupCodes) && backupCodes.map((code, index) => <li key={index}>{code}</li>)}
          </ul>
          <button type="button" className="btn-style1 btn-security-submit" onClick={handlePrintBackupCodes}>{t('Print')}</button>
        </div>
      </Modal>
    </section>
  );
}

export default SecurityMfa;
